<script setup lang="ts">
import { computed } from 'vue';

import { statusText } from '@/utils/format';

const props = defineProps<{ value: string | null | undefined; label?: string }>();

// 默认就翻译成中文：让每个调用点自己传 label，必然有人漏传，
// 结果就是页面上散落着 pending / not_submitted 这类英文码。
const text = computed(() => props.label || statusText(props.value) || '未定义');

/*
  0916 Tony:「项目状态标识不清楚, 只有灰色英文字母体现」。

  两件事一起坏的：
  · **中文值一个都不认**。判据只比英文码，而项目状态在库里是「进行中」——
    于是 14 个在跑的项目和 11 个已删除的项目**一样是灰的**。
  · **「还没开始」和「已经结束」挤在同一个灰里**。草稿、已删除、已归档
    在这张表上长得一模一样，可它们要人做的事正好相反。

  所以加两档：`draft`（还没开始，空心圈）和 `ended`（已经结束，×）。
  中英文一起认——库里两种都有（[[one-judgement-per-concept]]）。
*/
const RISK = ['risk', 'overdue', 'rejected', 'blocked', 'critical', 'disabled', '受阻', '已退回', '已逾期'];
const WARNING = ['warning', 'due-soon', 'pending', 'in_review', 'attention', 'paused', 'legacy_orphan',
  '待处理', '审批中', '已暂停', '待归属', '即将到期'];
const NORMAL = ['normal', 'approved', 'closed', 'published', 'active', 'completed', 'released', 'in_progress',
  '进行中', '已完成', '已批准', '已发布', '已关闭', '正常'];
/** 还没开始：要人去启动它 */
const DRAFT = ['draft', 'quotation', 'not_submitted', '草稿', '报价', '未提交'];
/** 已经结束：不用再管它，但也别让它冒充在跑的 */
const ENDED = ['deleted', 'archived', 'cancelled', 'superseded', '已删除', '已归档', '已取消', '已作废'];

const tone = computed(() => {
  const raw = String(props.value || '').trim();
  const value = raw.toLowerCase();
  const has = (list: readonly string[]) => list.includes(value) || list.includes(raw);
  if (has(RISK)) return 'risk';
  if (has(ENDED)) return 'ended';
  if (has(DRAFT)) return 'draft';
  if (has(WARNING)) return 'warning';
  if (has(NORMAL)) return 'normal';
  return 'neutral';
});
</script>

<script lang="ts">
/**
 * 形状随语义变化：约 8% 的男性有色觉障碍，只靠红/黄/绿三色区分状态他们分不出。
 * 文字标签本来就有，这里再加一层形状冗余：风险=三角、关注=方块、正常=圆点。
 */
const TONE_GLYPH: Record<string, string> = {
  risk: '▲',
  warning: '■',
  normal: '●',
  /** 空心圈 = 还没开始；实心圆是在跑的，一眼能分 */
  draft: '○',
  /** 叉 = 已经结束 */
  ended: '×',
  neutral: '·',
};
export { TONE_GLYPH };
</script>

<template>
  <span class="status-chip" :class="`is-${tone}`">
    <i aria-hidden="true">{{ TONE_GLYPH[tone] }}</i>{{ text }}
  </span>
</template>
